//#region IMPORTS
import {
  setInstoreOrderParams,
  clearInstoreOrderParams,
  fetchInstoreOrdersAsync,
  fetchShippableInstoreOrdersAsync,
  fetchApprovableInstoreOrdersAsync,
  fetchInstoreFlowCountsAsync,
  instoreOrdersSelectors,
} from "../../../features/instoreOrders/instoreOrdersSlice";
import {
  InstorePresetKey,
  InstoreTypeBucket,
  INSTORE_BASE_PARAMS,
  INSTORE_TYPE_BUCKET_PARAMS,
  VALID_INSTORE_PRESETS,
  getInstoreDefaultPreset,
  isInstoreTypeBucket,
  resolveInstorePresetFilter,
} from "./instorePresets";
import { useAppDispatch, useAppSelector } from "../../../store/configureStore";
import { useHistory, useLocation } from "react-router-dom";
import { SHOP_TAB, TAB_LABELS } from "../tabs";
import { fetchFilters } from "../../../features/orders/ordersSlice";
import { ORDERSTATUS } from "../../../models/order";
import { useEffect } from "react";
import ToggleButton from "../../genericButtons/toggleButton/ToggleButton";
import InstoreOrdersBulkActions from "../../instoreOrders/InstoreOrdersBulkActions";
import InstoreOrdersContainer from "../../instoreOrders/InstoreOrdersContainer";
import InstorePresetPills from "./InstorePresetPills";
import usePickerManager from "../../../hooks/usePickerManager";
import useAdminManager from "../../../hooks/useAdminManager";
import useHQmanager from "../../../hooks/useHQmanager";
import useActiveWeekInstoreTemplate from "../../../hooks/useActiveWeekInstoreTemplate";
import classNames from "../../../helpers/classNames";
import { Tooltip } from "react-tooltip";
//#endregion

export default function InstoreOrdersView() {
  //#region SETUP
  const dispatch = useAppDispatch();
  const location = useLocation();
  const history = useHistory();
  const { user } = useAppSelector((state) => state.account);
  const {
    instoreOrdersLoaded,
    instoreOrders_ordered,
    instoreOrders_pending,
    instoreOrders_inProgress,
    instoreOrder_params,
    instoreFlowCounts,
    bulkRefreshing,
  } = useAppSelector(({ instoreOrders }) => instoreOrders);
  const { filters_loaded } = useAppSelector(({ orders }) => orders);

  const isPicker = usePickerManager(user);
  const isAdmin = useAdminManager(user);
  const isHq = useHQmanager(user);

  // Most recent active "Week" instore template within today's date window
  const activeWeekTemplate = useActiveWeekInstoreTemplate();

  const searchParams = new URLSearchParams(location.search);
  const rawPreset = searchParams.get("preset") as InstorePresetKey | null;
  const defaultPreset = getInstoreDefaultPreset(isHq, isAdmin, isPicker);
  const presetKey: InstorePresetKey =
    rawPreset && VALID_INSTORE_PRESETS.includes(rawPreset)
      ? rawPreset
      : defaultPreset;
  const { order_status, order_type_instore_name, exclude_order_type, paramsOverride } =
    resolveInstorePresetFilter(presetKey);

  // "Archived only" toggle (top-right). When on, the page shows a normal
  // server-paginated list of Archived orders, overriding the preset's own
  // status/type filters and disabling the selection/workflow machinery.
  const archivedOnly = searchParams.get("archived") === "1";

  // Optional order-type bucket carried alongside a flow preset (picker/admin).
  // It narrows the flow list to one type; the preset's own type wins if set.
  const rawType = searchParams.get("type");
  const activeType: InstoreTypeBucket | null = isInstoreTypeBucket(rawType)
    ? rawType
    : null;
  // The active type filter: the preset's own filter (positive or negative) wins;
  // otherwise the ?type= bucket narrows the flow list to Week or
  // Opening/Relocation.
  const typeParams: {
    order_type_instore_name?: string;
    exclude_order_type?: string;
  } =
    order_type_instore_name || exclude_order_type
      ? {
          ...(order_type_instore_name ? { order_type_instore_name } : {}),
          ...(exclude_order_type ? { exclude_order_type } : {}),
        }
      : activeType
        ? INSTORE_TYPE_BUCKET_PARAMS[activeType]
        : {};
  const typeFilterActive =
    !!typeParams.order_type_instore_name || !!typeParams.exclude_order_type;

  const isApprovePreset =
    presetKey === "approve" && (isHq || isAdmin || isPicker);
  // "manage" (picker + admin) and the legacy "handle" key (admin, kept for old
  // bookmarks) hold the Ordered orders → set them in progress here.
  const isManagePreset =
    (presetKey === "manage" && (isPicker || isAdmin)) ||
    (presetKey === "handle" && isAdmin);
  // "send" holds the In progress orders → ship them here.
  const isSendPreset = presetKey === "send" && (isPicker || isAdmin);
  // "deliver" holds the Shipped orders → mark delivered here.
  const isDeliverPreset = presetKey === "deliver" && (isPicker || isAdmin);
  // "archive" holds the Delivered orders → archive them in bulk here.
  const isArchivePreset = presetKey === "archive" && (isPicker || isAdmin);
  const workflowActive =
    !archivedOnly &&
    (isApprovePreset ||
      isManagePreset ||
      isSendPreset ||
      isDeliverPreset ||
      isArchivePreset);

  // Preset-route guard: a flow preset that this role may not use is off-limits
  // → bounce to the role default. The overview presets (week / opening-
  // relocation / all) are open to everyone, so they never trip this.
  const presetNotAllowed =
    (presetKey === "approve" && !isApprovePreset) ||
    ((presetKey === "manage" || presetKey === "handle") && !isManagePreset) ||
    (presetKey === "send" && !isSendPreset) ||
    (presetKey === "deliver" && !isDeliverPreset) ||
    (presetKey === "archive" && !isArchivePreset);
  const orderCount = useAppSelector(instoreOrdersSelectors.selectTotal);

  // On a non-workflow preset (all / week / opening) choosing a status in the
  // filter bar unlocks high-limit, client-side selection — but ONLY to export
  // the filtered list (no approve / set-in-progress / ship).
  const hasStatusFilter =
    typeof instoreOrder_params.order_status === "string" &&
    instoreOrder_params.order_status !== "";
  const exportFilterActive =
    (isPicker || isAdmin) && !workflowActive && hasStatusFilter && !archivedOnly;
  const selectionActive = workflowActive || exportFilterActive;
  const desiredLimit = selectionActive ? 10000 : INSTORE_BASE_PARAMS.limit;

  // On a fresh landing (no explicit preset):
  //  - picker / admin land on the "all" overview (no auto-redirect)
  //  - HQ jumps straight to Approve when there is pending work
  // While a redirect is pending we must NOT set params or fetch with the default
  // "all" preset, otherwise that fetch can land after the redirected one and show
  // a mix of statuses.
  const redirectTarget: InstorePresetKey | null =
    rawPreset ? null : isHq && instoreOrders_pending > 0 ? "approve" : null;
  //#endregion

  //#region SIDE EFFECTS
  useEffect(() => {
    // Bounce a role off a preset it may not use, to its default landing.
    if (presetNotAllowed) {
      history.replace(`/orders?tab=${SHOP_TAB.instore}`);
      return;
    }
    if (redirectTarget) {
      history.replace(
        `/orders?tab=${SHOP_TAB.instore}&preset=${redirectTarget}`,
      );
    }
  }, [presetNotAllowed, redirectTarget, history]);

  useEffect(() => {
    if (redirectTarget || presetNotAllowed) return;
    dispatch(clearInstoreOrderParams());
    dispatch(
      setInstoreOrderParams(
        archivedOnly
          ? {
              ...INSTORE_BASE_PARAMS,
              order_status: ORDERSTATUS.archived,
            }
          : {
              ...INSTORE_BASE_PARAMS,
              ...(order_status ? { order_status } : {}),
              ...typeParams,
              ...(paramsOverride ?? {}),
            },
      ),
    );
  }, [dispatch, presetKey, activeType, archivedOnly, redirectTarget]);

  useEffect(() => {
    if (redirectTarget || presetNotAllowed) return;
    const fetchOnload = async () => {
      if (!instoreOrdersLoaded) {
        await dispatch(fetchInstoreOrdersAsync());
        if (isApprovePreset) {
          await dispatch(fetchApprovableInstoreOrdersAsync());
        }
        if (isManagePreset) {
          await dispatch(fetchShippableInstoreOrdersAsync());
        }
      }
    };
    fetchOnload();
  }, [
    instoreOrdersLoaded,
    dispatch,
    isApprovePreset,
    isManagePreset,
    redirectTarget,
  ]);

  useEffect(() => {
    !filters_loaded && dispatch(fetchFilters());
  }, [filters_loaded, dispatch]);

  // Per-type flow-pill counts (Week vs Opening/Relocation) — picker/admin only.
  useEffect(() => {
    if (isPicker || isAdmin) dispatch(fetchInstoreFlowCountsAsync());
  }, [isPicker, isAdmin, dispatch]);

  // When a status change (bulk or per-row) empties the current flow list,
  // advance to the next flow pill with work (Approve → Manage → Send), else to
  // the "all" overview. HQ has no Manage/Send, so HQ's Approve falls back to all.
  useEffect(() => {
    if (redirectTarget || presetNotAllowed) return;
    if (bulkRefreshing) return;
    if (!workflowActive || !instoreOrdersLoaded || orderCount > 0) return;
    const canHandle = isPicker || isAdmin;
    // Sum the per-type buckets so the chain knows whether the next pill has
    // anything to do (Deliver lives on the Shipped status).
    const shippedTotal =
      instoreFlowCounts.shipped.week +
      instoreFlowCounts.shipped["opening-relocation"];
    let target: InstorePresetKey = "all";
    if (isApprovePreset && canHandle) {
      target =
        instoreOrders_ordered > 0
          ? "manage"
          : instoreOrders_inProgress > 0
            ? "send"
            : shippedTotal > 0
              ? "deliver"
              : "all";
    } else if (isManagePreset) {
      target =
        instoreOrders_inProgress > 0
          ? "send"
          : shippedTotal > 0
            ? "deliver"
            : "all";
    } else if (isSendPreset) {
      target = shippedTotal > 0 ? "deliver" : "all";
    }
    history.replace(`/orders?tab=${SHOP_TAB.instore}&preset=${target}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    instoreOrdersLoaded,
    orderCount,
    workflowActive,
    redirectTarget,
    bulkRefreshing,
  ]);

  // Keep the fetch limit in sync when a status filter is toggled at runtime on a
  // non-workflow preset (the preset effect above does not re-run for that).
  useEffect(() => {
    if (redirectTarget || presetNotAllowed) return;
    if (instoreOrder_params.limit !== desiredLimit) {
      dispatch(setInstoreOrderParams({ limit: desiredLimit }));
    }
  }, [desiredLimit, instoreOrder_params.limit, dispatch, redirectTarget]);
  //#endregion

  //#region RENDER
  const setPreset = (key: InstorePresetKey, type?: InstoreTypeBucket | null) => {
    const typeParam = type ? `&type=${type}` : "";
    history.replace(
      `/orders?tab=${SHOP_TAB.instore}&preset=${key}${typeParam}`,
    );
  };

  // Toggle the "Archived only" view. Always lands on the "all" preset, adding
  // (or dropping) the ?archived=1 flag.
  const toggleArchived = () => {
    const archivedParam = archivedOnly ? "" : "&archived=1";
    history.replace(
      `/orders?tab=${SHOP_TAB.instore}&preset=all${archivedParam}`,
    );
  };

  // The "Archived only" toggle only makes sense on the plain "All" overview —
  // on a workflow/type preset it would be confusing.
  const canViewArchived =
    (isPicker || isAdmin || isHq) && presetKey === "all";

  const bulkPanel = workflowActive ? (
    <InstoreOrdersBulkActions
      mode={
        isApprovePreset
          ? "approve"
          : isSendPreset
            ? "ship"
            : isDeliverPreset
              ? "delivered"
              : isArchivePreset
                ? "archive"
                : "in-progress"
      }
    />
  ) : exportFilterActive ? (
    // Status-filtered overview: export only.
    <InstoreOrdersBulkActions mode="export" />
  ) : null;

  const headerControls = (
    <div className="flex items-center gap-4">
      {canViewArchived && (
        <ToggleButton
          label="Archived only"
          labelRight
          refresh
          startValue={archivedOnly}
          toggleHandler={toggleArchived}
        />
      )}
      {bulkPanel}
    </div>
  );

  const pills = (
    <div className="flex flex-1 items-center gap-3 pr-10">
      <InstorePresetPills
        activeKey={presetKey}
        activeType={activeType}
        onSelect={setPreset}
        isPicker={isPicker}
        isAdmin={isAdmin}
        isHq={isHq}
      />

      {/* Picker can order on behalf of a store — always visible; disabled
          with a tooltip when no week template is currently active. */}
      {isPicker && (
        <span
          data-tooltip-id={
            !activeWeekTemplate ? "picker-new-instore-tooltip" : undefined
          }
          data-tooltip-content={
            !activeWeekTemplate
              ? "No active week order available right now"
              : undefined
          }
        >
          <button
            disabled={!activeWeekTemplate}
            className={classNames(
              !activeWeekTemplate
                ? "bg-emerald-700/40 cursor-not-allowed"
                : "bg-emerald-700 hover:bg-emerald-600",
              "rounded-md px-3 py-1.5 text-sm font-semibold text-white",
            )}
            onClick={() => {
              if (!activeWeekTemplate) return;
              history.push(`/promos/instore/${activeWeekTemplate.id}`);
            }}
          >
            New instore order
          </button>
          {!activeWeekTemplate && (
            <Tooltip id="picker-new-instore-tooltip" place="top" />
          )}
        </span>
      )}
    </div>
  );

  return (
    <div
      aria-busy={bulkRefreshing}
      className={
        bulkRefreshing
          ? "pointer-events-none opacity-60 transition-opacity"
          : "transition-opacity"
      }
    >
      <InstoreOrdersContainer
        title={TAB_LABELS[SHOP_TAB.instore]}
        headerExtra={pills}
        hideStatusFilter={!!order_status || archivedOnly}
        hideTypeFilter={typeFilterActive}
        bulkPanel={headerControls}
        enableSelection={selectionActive}
        clientPaginate={selectionActive}
      />
    </div>
  );
  //#endregion
}
